import { CalendarDate, getLocalTimeZone, startOfMonth, today } from '@internationalized/date';
import * as aq from 'arquero';

import { filterByDateRange } from '../../../providers/TransactionsProvider';

import type { ColumnTable } from 'arquero';

export interface IDailyCumulativeSpendDataPoint {
  day: number; // 1-31
  spending: number | null; // cumulative spend for current month (null for future days)
  previousSpending: number | null; // cumulative spend for last month
}

export interface IProcessedDailyCumulativeSpendData {
  chartData: IDailyCumulativeSpendDataPoint[];
  currentTotal: number;
  previousTotal: number;
  currentMonthDaysElapsed: number;
  lastMonthTotalDays: number;
}

interface IRollupItem {
  day: number;
  spending: number;
}

/**
 * Get the number of days in a month
 */
function getDaysInMonth(year: number, month: number): number {
  // Create date for first day of next month, then subtract one day
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const lastDayOfMonth = new CalendarDate(nextYear, nextMonth, 1).subtract({ days: 1 });
  return lastDayOfMonth.day;
}

/**
 * Process transactions to get daily spending totals for a specific month
 */
function getDailySpending(transactions: ColumnTable, year: number, month: number, maxDay: number): Map<number, number> {
  const startDate = new CalendarDate(year, month, 1);
  const endDate = new CalendarDate(year, month, maxDay);

  // Filter by date range
  const filteredTable = filterByDateRange(transactions, startDate, endDate);

  // Filter to only spending (positive amounts in Plaid = spending)
  const spendingTable = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount > 0));

  // Group by day of month
  const rollup = spendingTable
    .groupby({
      day: (d: { date: CalendarDate }) => d.date.day,
    })
    .rollup({
      spending: aq.op.sum('amount'),
    })
    .orderby('day');

  const data = rollup.objects() as IRollupItem[];

  // Convert to map
  const spendingMap = new Map<number, number>();
  for (const item of data) {
    spendingMap.set(item.day, item.spending);
  }

  return spendingMap;
}

/**
 * Convert daily spending to cumulative spending
 */
function toCumulativeSpending(dailySpending: Map<number, number>, maxDay: number): Map<number, number> {
  const cumulativeMap = new Map<number, number>();
  let cumulative = 0;

  for (let day = 1; day <= maxDay; day++) {
    cumulative += dailySpending.get(day) ?? 0;
    cumulativeMap.set(day, Number(cumulative.toFixed(2)));
  }

  return cumulativeMap;
}

/**
 * Process transaction data into daily cumulative spending comparing this month vs last month.
 * - Current month: cumulative spend from day 1 to today's date
 * - Last month: cumulative spend for the full month (28, 29, 30, or 31 days)
 */
export function processDailyCumulativeSpendData(transactions: ColumnTable | null): IProcessedDailyCumulativeSpendData {
  if (!transactions || transactions.numRows() === 0) {
    return {
      chartData: [],
      currentTotal: 0,
      previousTotal: 0,
      currentMonthDaysElapsed: 0,
      lastMonthTotalDays: 0,
    };
  }

  const now = today(getLocalTimeZone());
  const currentMonth = startOfMonth(now);
  const lastMonth = currentMonth.subtract({ months: 1 });

  const currentMonthDaysElapsed = now.day;
  const lastMonthTotalDays = getDaysInMonth(lastMonth.year, lastMonth.month);

  // Get daily spending for current month (up to today)
  const currentMonthDailySpending = getDailySpending(
    transactions,
    currentMonth.year,
    currentMonth.month,
    currentMonthDaysElapsed
  );

  // Get daily spending for last month (full month)
  const lastMonthDailySpending = getDailySpending(transactions, lastMonth.year, lastMonth.month, lastMonthTotalDays);

  // Convert to cumulative
  const currentMonthCumulative = toCumulativeSpending(currentMonthDailySpending, currentMonthDaysElapsed);
  const lastMonthCumulative = toCumulativeSpending(lastMonthDailySpending, lastMonthTotalDays);

  // Build chart data - x-axis is 1-31
  const maxDays = 31;
  const chartData: IDailyCumulativeSpendDataPoint[] = [];

  for (let day = 1; day <= maxDays; day++) {
    const currentSpending = day <= currentMonthDaysElapsed ? (currentMonthCumulative.get(day) ?? 0) : null;
    const previousSpending = day <= lastMonthTotalDays ? (lastMonthCumulative.get(day) ?? 0) : null;

    chartData.push({
      day,
      spending: currentSpending,
      previousSpending,
    });
  }

  // Get totals
  const comparisonDay = Math.min(currentMonthDaysElapsed, lastMonthTotalDays);
  const currentTotal = currentMonthCumulative.get(currentMonthDaysElapsed) ?? 0;
  const previousTotal = lastMonthCumulative.get(comparisonDay) ?? 0;

  return {
    chartData,
    currentTotal: Number(currentTotal.toFixed(2)),
    previousTotal: Number(previousTotal.toFixed(2)),
    currentMonthDaysElapsed,
    lastMonthTotalDays,
  };
}
